/**
 * Parses a Space Organizer suggestion `title` into an ordered list of segments.
 *
 * Suggestion titles can reference content in two formats:
 *
 * 1. **Bare ID reference** (current API format):
 *    `Rename {7216354687} to "Archive"`
 *    The ID is resolved via the `titleReferences` map supplied by the caller.
 *
 * 2. **Inline object literal** (legacy format):
 *    `Rename {contentId:7003778614,title:"New Folder",spaceKey:"~tmarra",contentType:"folder"} to "Archive"`
 *    Self-contained — no external lookup needed.
 *
 * Each `{...}` segment is evaluated independently: first as a bare ID lookup
 * against `titleReferences`, then as an inline object literal. When either
 * succeeds a `link` segment is emitted so the renderer can replace it with a
 * content link. Everything else is preserved verbatim as a `text` segment.
 */

import type { TitleReferences } from '../__types__/SpaceSuggestion.ts';

export type TitleSegment =
	| { type: 'text'; value: string }
	| {
			type: 'link';
			title: string;
			contentId: string;
			/** Drives the link icon only; absent/unknown falls back to the page icon. */
			contentType?: string;
	  };

// Matches a single flat `{...}` object. The embedded objects never nest, so a
// non-greedy "no inner braces" character class is sufficient and avoids
// over-matching across two separate objects in the same title.
const OBJECT_PATTERN = /\{[^{}]*\}/g;

// Quotes object keys so the relaxed literal becomes valid JSON. Anchoring on
// `{` or `,` means only keys are quoted — a `:` inside a string value (which is
// preceded by a `"`, not `{`/`,`) is left untouched.
const UNQUOTED_KEY_PATTERN = /([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g;

type LinkSegment = Extract<TitleSegment, { type: 'link' }>;

/**
 * Attempts to resolve a bare `{contentId}` reference against `titleReferences`.
 * Returns a `link` segment when the ID is found, or `null`.
 */
function tryResolveBareId(raw: string, titleReferences?: TitleReferences): LinkSegment | null {
	if (!titleReferences) {
		return null;
	}
	// Strip the surrounding braces and trim whitespace to get the bare ID.
	const id = raw.slice(1, -1).trim();
	if (!id) {
		return null;
	}
	const ref = titleReferences[id];
	if (!ref || !ref.title || !ref.contentId) {
		return null;
	}
	return {
		type: 'link',
		title: ref.title,
		contentId: String(ref.contentId),
		contentType: ref.contentType || undefined,
	};
}

/**
 * Attempts to interpret a raw `{...}` substring as a content-link object
 * (legacy inline format). Returns a `link` segment when it parses and carries
 * all required fields, or `null` so the caller can fall back to rendering the
 * raw text.
 */
function tryParseLinkSegment(raw: string): LinkSegment | null {
	const normalized = raw.replace(UNQUOTED_KEY_PATTERN, '$1"$2":');

	let parsed: unknown;
	try {
		parsed = JSON.parse(normalized);
	} catch {
		return null;
	}

	if (typeof parsed !== 'object' || parsed === null) {
		return null;
	}

	const { title, contentId, contentType } = parsed as Record<string, unknown>;

	const hasContentId = typeof contentId === 'string' || typeof contentId === 'number';

	if (typeof title !== 'string' || !title) {
		return null;
	}
	if (!hasContentId || !contentId) {
		return null;
	}

	return {
		type: 'link',
		title,
		contentId: String(contentId),
		contentType: typeof contentType === 'string' ? contentType : undefined,
	};
}

export function parseSuggestionTitle(
	title: string,
	titleReferences?: TitleReferences,
): TitleSegment[] {
	const segments: TitleSegment[] = [];

	// Appends text, coalescing with a preceding text segment so verbatim
	// fallbacks (an unparseable `{...}` between two words) stay one segment.
	const pushText = (value: string) => {
		if (!value) {
			return;
		}
		const last = segments[segments.length - 1];
		if (last?.type === 'text') {
			last.value += value;
		} else {
			segments.push({ type: 'text', value });
		}
	};

	// Local regex instance so the shared `lastIndex` state never leaks across calls.
	const pattern = new RegExp(OBJECT_PATTERN.source, 'g');
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	while ((match = pattern.exec(title)) !== null) {
		const raw = match[0];
		const start = match.index;

		pushText(title.slice(lastIndex, start));

		// Try bare ID resolution first (current API format), then fall back to
		// inline object literal parsing (legacy format).
		const link = tryResolveBareId(raw, titleReferences) ?? tryParseLinkSegment(raw);
		if (link) {
			segments.push(link);
		} else {
			pushText(raw);
		}

		lastIndex = start + raw.length;
	}

	pushText(title.slice(lastIndex));

	return segments;
}
